import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  ChipsInput,
  CustomButton,
  CustomIcon,
  CustomInput, defaultValidateMessages, DiscardModal, DropdownElement,
  EIconName, emailPattern,
  InputReadOnly, stringPattern
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './general-data.scss';

// Store entities
import { fetchSpecializations } from '../../../../store/core/core.thunks';
import { updateCounsellorProfile } from '../../../../store/current-counsellor/current-counsellor.thunks';
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';
import { getCoreState } from '../../../../store/core/core.selectors';

// Components
import { CountryOption } from '../../country-option/country-option';

// Models
import { ICounsellor } from '../../../../interfaces/counsellors.interfaces';
import { ICountry, ISpeciality } from '../../../../interfaces/core.interfaces';

type TGeneralDataProps = {
  counsellor: ICounsellor;
  isCurrentAdmin?: boolean;
};

export const GeneralData: FC<TGeneralDataProps> = (
  { counsellor, isCurrentAdmin = false }: TGeneralDataProps
) => {
  const [form] = Form.useForm<{
    first_name: string;
    last_name: string;
    email: string;
    country: any, // { code: string, flag: string name: string };
    languages: string[];
  }>();

  const { languages, countries } = useSelector(getCoreState);

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const generateOptions = (options: { name: string, code: string }[], isCountry: boolean = false): {
    label: ReactNode,
    value: string,
    disabled?: boolean,
  }[] => {
    return options.map((option) => (
      { label: isCountry ? <CountryOption country={ option as ICountry }/> : option.name, value: option.name })
    )
  };

  const languagesOptions = generateOptions(languages.list);
  const countriesOptions = [{ label: 'Leave Empty', value: '---' }, ...generateOptions(countries.list, true)];

  const languagesChips = counsellor.languages.map(lang => lang.name);

  const generateOptionsIdArray = (
    options: { name: string, code: string }[], names: string[]
  ): Array<string> => {
    return options
      .filter((opt) => names?.includes(opt?.name))
      .map((opt) => opt.code)
  };

  const getCountryOptionId = (
    options: { name: string, code: string }[], name: string
  ): string => {
    return options
      .filter((opt) => name === opt?.name)
      .map((opt) => opt.code)?.[0] ?? counsellor.country?.code
  };

  const onValuesChange = (_: { [key: string]: string | string[] }, {
    first_name, last_name, email, country, languages
  }: {
    first_name: string,
    last_name: string,
    email: string,
    country: string | { value: string },
    languages: string[]
  }): void => {

    if (country === '---') {
      form.setFieldValue('country', null);
    }

    if (first_name !== undefined
      && last_name !== undefined
      && email !== undefined
      && first_name !== ''
      && last_name !== ''
      && email !== ''
      && first_name.length <= 30
      && last_name.length <= 30
      && last_name.length <= 60) {
      const isFirstNameChanged = first_name !== counsellor?.first_name;
      const isLastNameChanged = last_name !== counsellor?.last_name;
      const isEmailChanged = email !== counsellor?.email;
      const isCountryChanged =
        (typeof country === 'string' && country !== counsellor?.country?.name) ||
        (typeof country !== 'string' && country?.value !== counsellor?.country?.name);
      const isLanguagesChanged =
        counsellor.languages.some(lang => !languages.includes(lang.name)) || languages.length !== counsellor?.languages?.length;

      const isHasChanges =
        isFirstNameChanged ||
        isLastNameChanged ||
        isEmailChanged ||
        isCountryChanged ||
        isLanguagesChanged;

      setHasChanges(isHasChanges && !!first_name && !!last_name && !!email && new RegExp(emailPattern).test(email));
    } else {
      setHasChanges(false);
    }
  }

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    toggleEditMode();
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      form.resetFields();
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();
      return;
    }
    toggleDiscardModal();
  }

  const fetchSpecializationsList = async (speciality: ISpeciality[]): Promise<void> => {
    if (!speciality?.length) return;
    dispatch(fetchSpecializations({ speciality }));
  }

  const saveChanges = async (): Promise<void> => {

    try {
      const { languages: languagesNames, country, ...allFields } = await form.validateFields();
      setSavingChanges(true);

      const { payload } = await dispatch(updateCounsellorProfile({
        counsellor: {
          ...allFields,
          country_code: country === null ? null : getCountryOptionId(countries.list, country),
          languages: generateOptionsIdArray(languages.list, languagesNames)
        },
        worker_id: counsellor.id,
        isCurrentAdmin
      }));

      setSavingChanges(false);
      if (!payload) return;
      setEditMode(false);
      setHasChanges(false);
      form.resetFields();
    } catch (e) {
      const fieldErrors = form.getFieldsError();
      setHasChanges(false);
      dispatch(openNotification({
        type: 'error',
        description: fieldErrors.find(field => !!field.errors.length)?.errors[0]
      }));
    }
  }

  useEffect(() => {
    fetchSpecializationsList(counsellor.specialities);
  }, []);

  return (
    <div className="general-data">
      <DiscardModal
        open={ discardOpen }
        onCancel={ onDiscardChangesHandler }
        title={ 'Are you sure you want to discard changes?' }
      />
      <div className="general-data--label personal-data--label">
        <div className="label-title">
          General Data
        </div>
        <div className="label-actions">
          { !editMode &&
            <CustomButton type={ 'text' }
                          text={ 'Edit' }
                          size={ 'small' }
                          onClick={ toggleEditMode }/>
          }

          { editMode && <>
            { hasChanges && <CustomButton type={ 'primary' }
                                          text={ 'Save' }
                                          size={ 'small' }
                                          loading={ savingChanges }
                                          onClick={ saveChanges }/> }
            <CustomButton type={ 'text' }
                          text={ 'Cancel' }
                          size={ 'small' }
                          disabled={ savingChanges }
                          onClick={ onCancelChangesHandler }/>
          </> }
        </div>
      </div>
      <Form form={ form } className="general-data--content personal-data--content" onValuesChange={ onValuesChange }>
        <div className="content-item split-items">
          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'First Name *' } value={ counsellor.first_name }/> }

            { editMode &&
              <Form.Item
                name={ 'first_name' }
                label={ 'First Name' }
                initialValue={ counsellor.first_name }
                messageVariables={ { fieldLabel: 'first name' } }
                rules={ [
                  { required: true, max: 30 },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || new RegExp(stringPattern).test(getFieldValue('first_name'))) {
                        return Promise.resolve();
                      }
                      if (value.length > 30) {
                        return Promise.resolve();
                      }
                      setSavingChanges(true);
                      return Promise.reject(new Error(defaultValidateMessages.invalidFirstNameFormat));
                    }
                  })
                ] }>
                <CustomInput type={ 'text' } placeholder={ 'First Name *' }/>
              </Form.Item> }
          </div>

          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'Last Name *' } value={ counsellor.last_name }/> }

            { editMode &&
              <Form.Item
                name={ 'last_name' }
                label={ 'Last Name' }
                initialValue={ counsellor.last_name }
                messageVariables={ { fieldLabel: 'last name' } }
                rules={ [
                  { required: true, max: 30 },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || new RegExp(stringPattern).test(getFieldValue('last_name'))) {
                        return Promise.resolve();
                      }
                      if (value.length > 30) {
                        return Promise.resolve();
                      }
                      setSavingChanges(true);
                      return Promise.reject(new Error(defaultValidateMessages.invalidLastNameFormat));
                    }
                  })
                ] }>
                <CustomInput type={ 'text' } placeholder={ 'Last Name *' }/>
              </Form.Item> }
          </div>
        </div>

        <div className="content-item split-items">
          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'Email *' } value={ counsellor.email } textEllipsis={ true }
                                          icon={ <CustomIcon name={ EIconName.Mail }/> }/> }

            { editMode && <Form.Item
              name={ 'email' }
              initialValue={ counsellor.email }
              messageVariables={ { fieldLabel: 'Email' } }
              rules={ [
                { required: true },
                { max: 60 },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || new RegExp(emailPattern).test(getFieldValue('email'))) {
                      return Promise.resolve();
                    }
                    if (value.length > 60) {
                      return Promise.resolve();
                    }
                    setSavingChanges(true);
                    return Promise.reject(new Error(defaultValidateMessages.invalidEmailFormat));
                  }
                })] }>
              <CustomInput type={ 'email' } placeholder={ 'Email *' } disabled={ isCurrentAdmin } suffix={ <CustomIcon name={ EIconName.Mail }/> }/>
            </Form.Item> }
          </div>

          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'Country' } value={ counsellor.country?.flag ?
              <><img src={ counsellor.country?.flag }/>{ counsellor.country?.name }</> : ''
            }/> }

            { editMode &&
              <Form.Item name={ 'country' } initialValue={ counsellor.country?.name } valuePropName={ 'value' }>
                <DropdownElement options={ countriesOptions }
                                 withoutAll={ true }
                                 showSearch={ true }
                                 placeholder={ 'Country' }/>
              </Form.Item> }
          </div>
        </div>

        <div className="content-item languages-section">
          <div className="content-item--field">
            { !editMode && <InputReadOnly label={ 'Languages' } chips={ languagesChips }/> }

            { editMode && <Form.Item name={ 'languages' } initialValue={ counsellor.languages.map(s => s.name) }>
              <ChipsInput options={ languagesOptions }
                          placeholder={ 'Language(s)' }
                          hint={ 'Start typing to add a language' }/>
            </Form.Item> }
          </div>
        </div>
      </Form>
    </div>
  );
}
